import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  ArrowLeft,
  ChefHat,
  Search,
  Home,
  Check,
  Loader2,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useProducts } from '@/hooks/useProducts';
import { useCurrency } from '@/hooks/useCurrency';
import { useOrders } from '@/hooks/useOrders';
import { toast } from 'sonner';
import { useKioskDevice, type KioskDeviceInfo } from '@/hooks/useKiosk';

interface CartItem {
  id: string;
  product_id: string;
  name: string;
  price: number;
  quantity: number;
  image_url?: string | null;
}

type KioskView = 'categories' | 'products' | 'cart' | 'checkout' | 'confirmation';

export default function Kiosk() {
  const navigate = useNavigate();
  const { validateToken, isRegistered } = useKioskDevice();
  
  const [deviceInfo, setDeviceInfo] = useState<KioskDeviceInfo | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  
  // Use location from device info
  const locationId = deviceInfo?.location_id || null;
  
  const { products, categories, isLoading } = useProducts({ locationId });
  const { formatCurrency } = useCurrency();
  const { createOrderAndSendToKitchen } = useOrders(locationId);
  
  const [view, setView] = useState<KioskView>('categories');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Validate device on mount
  useEffect(() => {
    const checkDevice = async () => {
      if (!isRegistered()) {
        navigate('/kiosk/setup');
        return;
      }

      const info = await validateToken();
      if (!info) {
        navigate('/kiosk/setup');
        return;
      }

      setDeviceInfo(info);
      setIsValidating(false);
    };

    checkDevice();
  }, []);

  // Reset inactivity timer on user interaction
  useEffect(() => {
    if (!deviceInfo) return;

    const resetTimer = () => {
      // Always clear using the ref (which is always current)
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      const timeoutMs = (deviceInfo.settings.timeout_seconds || 120) * 1000;
      inactivityTimerRef.current = setTimeout(() => {
        // Reset to start screen if inactive
        if (view !== 'categories' || cart.length > 0) {
          startOver();
          toast.success('Session Reset', {
        description: 'The kiosk has been reset due to inactivity',
      });
        }
      }, timeoutMs);
    };

    // Set up event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'touchstart', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [deviceInfo, view, cart.length]);

  // Filter products by category and search
  const filteredProducts = useMemo(() => {
    let filtered = products || [];
    
    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }
    
    // Only show available products with positive prices (no subsidies in kiosk)
    filtered = filtered.filter(p => p.is_available && p.price > 0);
    
    return filtered;
  }, [products, selectedCategory, searchQuery]);

  // Cart calculations
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * 0.15;
  const total = subtotal + tax;

  // Add to cart
  const addToCart = (product: typeof products[0]) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          product_id: product.id,
          name: product.name,
          price: product.effective_price ?? product.price,
          quantity: 1,
          image_url: product.image_url,
        },
      ];
    });

    // Show brief toast
    toast('Added to cart', {
        description: 'product.name',
        duration: 1500,
      });
  };

  // Update quantity
  const updateQuantity = (itemId: string, delta: number) => {
    setCart(prev =>
      prev
        .map(item =>
          item.id === itemId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  };

  // Remove item
  const removeItem = (itemId: string) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Submit order
  const submitOrder = async () => {
    if (cart.length === 0 || !locationId) return;

    setIsSubmitting(true);
    try {
      const result = await createOrderAndSendToKitchen.mutateAsync({
        order_type: 'takeout', // Kiosk orders are typically takeout
        items: cart.map(item => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.price,
        })),
      });

      // Show confirmation
      setOrderNumber(result?.order?.order_number || 'N/A');
      setView('confirmation');
      clearCart();
    } catch (error) {
      toast.error('Order Failed', {
        description: 'Please try again or ask for assistance',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset to start
  const startOver = () => {
    setView('categories');
    setSelectedCategory(null);
    setSearchQuery('');
    setOrderNumber(null);
    clearCart();
  };

  // Select category
  const selectCategory = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setView('products');
  };

  // Go to setup
  const goToSetup = () => {
    navigate('/kiosk/setup');
  };

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 text-blaze-500 animate-spin" />
          <p className="text-white/60">Loading kiosk...</p>
        </div>
      </div>
    );
  }

  // Render based on view
  if (view === 'confirmation') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-8">
        <div className="text-center text-white max-w-lg">
          <div className="w-24 h-24 mx-auto bg-white rounded-full flex items-center justify-center mb-8">
            <Check className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Order Confirmed!</h1>
          <p className="text-xl mb-8 opacity-90">Your order is being prepared</p>
          <div className="bg-white/20 rounded-2xl p-8 mb-8">
            <p className="text-lg opacity-80">Order Number</p>
            <p className="text-6xl font-bold mt-2">{orderNumber}</p>
          </div>
          <p className="text-lg mb-8 opacity-80">Please wait for your number to be called</p>
          <Button
            size="lg"
            variant="secondary"
            className="text-lg h-14 px-8"
            onClick={startOver}
          >
            <Home className="h-5 w-5 mr-2" />
            Start New Order
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-black/30 backdrop-blur-sm px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {(view === 'products' || view === 'cart' || view === 'checkout') && (
              <Button
                variant="ghost"
                size="icon"
                className="text-white"
                onClick={() => {
                  if (view === 'products') {
                    setView('categories');
                    setSelectedCategory(null);
                  } else if (view === 'cart' || view === 'checkout') {
                    setView('products');
                  }
                }}
              >
                <ArrowLeft className="h-6 w-6" />
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">
                {deviceInfo?.location_name || 'Self-Order Kiosk'}
              </h1>
              <p className="text-white/60 text-sm">
                {view === 'categories' && 'Select a category to start'}
                {view === 'products' && categories?.find(c => c.id === selectedCategory)?.name}
                {view === 'cart' && 'Review your order'}
                {view === 'checkout' && 'Complete your order'}
              </p>
            </div>
          </div>

          {/* Search and Settings */}
          <div className="flex items-center gap-4">
            {(view === 'categories' || view === 'products') && (
              <div className="relative w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50" />
                <Input
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (e.target.value && view === 'categories') {
                      setView('products');
                    }
                  }}
                  className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
              </div>
            )}
            
            {/* Hidden setup button - triple tap to access */}
            <button
              onClick={goToSetup}
              className="p-2 text-white/20 hover:text-white/40 transition-colors"
              title="Kiosk Settings"
            >
              <Settings className="h-5 w-5" />
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-12 w-12 text-white animate-spin" />
            </div>
          ) : view === 'categories' ? (
            /* Categories Grid */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {categories?.map((category) => {
                const productCount = products?.filter(p => p.category_id === category.id && p.is_available).length || 0;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => selectCategory(category.id)}
                    className="group relative aspect-square rounded-3xl bg-gradient-to-br from-blaze-500 to-blaze-600 p-6 flex flex-col justify-end text-left overflow-hidden transition-transform hover:scale-105 active:scale-95"
                  >
                    {category.image_url ? (
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="absolute inset-0 w-full h-full object-cover opacity-60"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center opacity-20">
                        <ChefHat className="h-24 w-24 text-white" />
                      </div>
                    )}
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold text-white mb-1">{category.name}</h3>
                      <p className="text-white/80">{productCount} items</p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : view === 'products' ? (
            /* Products Grid */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  className="group bg-white rounded-2xl overflow-hidden text-left transition-transform hover:scale-105 active:scale-95 shadow-lg"
                >
                  {product.image_url ? (
                    <div className="aspect-square bg-gray-100">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="aspect-square bg-gradient-to-br from-blaze-100/50 to-blaze-50/50 flex items-center justify-center">
                      <ChefHat className="h-16 w-16 text-blaze-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 line-clamp-2">{product.name}</h3>
                    {product.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.description}</p>
                    )}
                    {deviceInfo?.settings.show_prices !== false && (
                      <p className="text-xl font-bold text-blaze-600 mt-2">
                        {formatCurrency(product.effective_price ?? product.price)}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : view === 'cart' ? (
            /* Cart View */
            <div className="max-w-2xl mx-auto">
              {cart.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingBag className="h-24 w-24 mx-auto text-white/30 mb-6" />
                  <h2 className="text-2xl font-bold text-white mb-2">Your cart is empty</h2>
                  <p className="text-white/60 mb-8">Add some items to get started</p>
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={() => setView('categories')}
                  >
                    Browse Menu
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-2xl p-4 flex items-center gap-4"
                    >
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-blaze-100 flex items-center justify-center">
                          <ChefHat className="h-8 w-8 text-blaze-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <p className="text-lg font-bold text-blaze-600 mt-1">
                          {formatCurrency(item.price * item.quantity)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-12 w-12 rounded-full"
                          onClick={() => updateQuantity(item.id, -1)}
                        >
                          <Minus className="h-5 w-5" />
                        </Button>
                        <span className="text-2xl font-bold w-10 text-center">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-12 w-12 rounded-full"
                          onClick={() => updateQuantity(item.id, 1)}
                        >
                          <Plus className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-12 w-12 text-red-500"
                          onClick={() => removeItem(item.id)}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : view === 'checkout' ? (
            /* Checkout View */
            <div className="max-w-lg mx-auto">
              <div className="bg-white rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex justify-between">
                      <span className="text-gray-600">
                        {item.quantity}x {item.name}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(item.price * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax (15%)</span>
                    <span>{formatCurrency(tax)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-2">
                    <span>Total</span>
                    <span className="text-blaze-600">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 space-y-4">
                <Button
                  size="lg"
                  className="w-full h-16 text-xl bg-green-600 hover:bg-green-700"
                  onClick={submitOrder}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="h-6 w-6 mr-2" />
                  )}
                  Pay at Counter
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full h-14"
                  onClick={() => setView('cart')}
                >
                  Back to Cart
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Cart Sidebar */}
      {(view === 'categories' || view === 'products') && (
        <aside className="w-96 bg-black/30 backdrop-blur-sm border-l border-white/10 flex flex-col">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" />
                Your Order
              </h2>
              {cart.length > 0 && (
                <Badge className="bg-blaze-500 text-white">
                  {cartItemCount} items
                </Badge>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="h-16 w-16 mx-auto text-white/30 mb-4" />
                <p className="text-white/60">Your cart is empty</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white/10 rounded-xl p-3 flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{item.name}</p>
                      <p className="text-sm text-blaze-400">
                        {formatCurrency(item.price)} × {item.quantity}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-white"
                        onClick={() => updateQuantity(item.id, -1)}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="text-white font-medium w-6 text-center">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-white"
                        onClick={() => updateQuantity(item.id, 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-white font-bold w-20 text-right">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="p-6 border-t border-white/10 space-y-4">
              <div className="space-y-2 text-white">
                <div className="flex justify-between text-white/60">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-white/60">
                  <span>Tax (15%)</span>
                  <span>{formatCurrency(tax)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span className="text-blaze-500">{formatCurrency(total)}</span>
                </div>
              </div>
              
              <Button
                size="lg"
                className="w-full h-14 text-lg bg-blaze-500 hover:bg-blaze-600"
                onClick={() => setView('checkout')}
              >
                Checkout
              </Button>
              <Button
                variant="ghost"
                className="w-full text-white/60 hover:text-white"
                onClick={clearCart}
              >
                Clear Cart
              </Button>
            </div>
          )}
        </aside>
      )}
    </div>
  );
}
